import React, { useEffect, useMemo, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  constants,
  NotificationManager,
  useApplicationState,
  usePaymentMethods,
  useSettings,
} from '@atlas/core';

import useTranslate from 'common/I18n/Interpreter';
import { FINISHED_BONUS_DETAILS_SOURCE } from 'common/Notifications/Popups/FinishedBonusDetails/finishedBonusDetailsShared';
import {
  FINISHED_BONUS_DETAILS_POPUP_ID,
  showFinishedBonusDetails,
} from 'common/Notifications/Popups/FinishedBonusDetails/showFinishedBonusDetails';
import BonusDetailBet from 'components/ClientProfile/Bonuses/BonusDetail/BonusDetailsBet';

import { resolveBonusImageUrl } from '../shared/bonusImage';

import {
  clearActiveBonusView,
  isActiveBonusView,
  rememberActiveBonusView,
} from './activeBonusViewStore';
import { BonusDetailContext } from './BonusDetailContext';
import BonusDetailHeader from './BonusDetailHeader';
import BonusDetailInfo from './BonusDetailInfo';
import BonusDetailTerms from './BonusDetailTerms';
import BonusDetailWager from './BonusDetailWager';
import BonusEligibleGames from './BonusEligibleGames';
import { bonusBuilder, freespinBuilder } from './builders';
import { isFinishedEntity } from './finishedBonusRouteUtils';
import { findActivatedInstanceId, resolveBonusEntity } from './resolveBonusEntity';

/**
 * FT-8194: finished statuses -> showFinishedBonusDetails(...) over the list
 * FT-8145 / FT-8103: available / unlocked / activated / in-progress -> status-specific pages
 */

const BonusDetailController = ({
  activateBonus,
  activateFreespin,
  availableTemplates,
  bonusId,
  cancelFreespin,
  clientBonuses,
  deactivateBonus,
  freespins,
  listUrl,
  loading,
}) => {
  const t = useTranslate();
  const history = useHistory();
  const { wallet } = useApplicationState();
  const { lang } = useSettings();
  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.MOBILE,
  );
  const lastOpenedBonusIdRef = useRef(null);

  const resolved = useMemo(
    () => resolveBonusEntity(bonusId, { availableTemplates, clientBonuses, freespins }),
    [availableTemplates, bonusId, clientBonuses, freespins],
  );

  const isFinished = isFinishedEntity(resolved);

  const redirectUrl = useMemo(() => {
    if (resolved) {
      return listUrl;
    }

    const activatedInstanceId = findActivatedInstanceId(bonusId, clientBonuses);

    return activatedInstanceId ? `${listUrl}/${activatedInstanceId}` : listUrl;
  }, [bonusId, clientBonuses, listUrl, resolved]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!bonusId) {
      lastOpenedBonusIdRef.current = null;
      clearActiveBonusView();
      NotificationManager.removeNotification(FINISHED_BONUS_DETAILS_POPUP_ID);

      return;
    }

    if (!resolved) {
      lastOpenedBonusIdRef.current = null;
      clearActiveBonusView();
      NotificationManager.removeNotification(FINISHED_BONUS_DETAILS_POPUP_ID);

      return;
    }

    if (!isFinished) {
      lastOpenedBonusIdRef.current = null;
      rememberActiveBonusView(bonusId);
      NotificationManager.removeNotification(FINISHED_BONUS_DETAILS_POPUP_ID);

      return;
    }

    if (isActiveBonusView(bonusId)) {
      clearActiveBonusView();
      lastOpenedBonusIdRef.current = null;
      NotificationManager.removeNotification(FINISHED_BONUS_DETAILS_POPUP_ID);
      history.replace({ pathname: listUrl });

      return;
    }

    if (lastOpenedBonusIdRef.current === bonusId) {
      return;
    }

    lastOpenedBonusIdRef.current = bonusId;

    const onNavigateClose = () => {
      lastOpenedBonusIdRef.current = null;
      history.replace({ pathname: listUrl });
    };

    const imageUrl = resolveBonusImageUrl(resolved.entity.imageLink) || '';

    if (resolved.kind === 'freespin') {
      showFinishedBonusDetails({
        freeSpin: resolved.entity,
        imageUrl,
        onNavigateClose,
        sourceType: FINISHED_BONUS_DETAILS_SOURCE.CLIENT_FREESPIN,
      });

      return;
    }

    showFinishedBonusDetails({
      bonus: resolved.entity,
      imageUrl,
      onNavigateClose,
      sourceType: FINISHED_BONUS_DETAILS_SOURCE.CLIENT_BONUS,
      wallet,
    });
  }, [bonusId, history, isFinished, listUrl, loading, resolved, wallet]);

  const entity = useMemo(() => {
    if (!resolved || isFinished) return null;

    if (resolved.kind === 'freespin') {
      return freespinBuilder({ bonus: resolved.entity, language: lang, t });
    }

    return bonusBuilder({ bonus: resolved.entity, paymentMethods, t, wallet });
  }, [isFinished, lang, paymentMethods, resolved, t, wallet]);

  const actions = useMemo(
    () => ({ activateBonus, activateFreespin, cancelFreespin, deactivateBonus }),
    [activateBonus, activateFreespin, cancelFreespin, deactivateBonus],
  );

  const value = useMemo(
    () => ({ actions, entity, kind: resolved?.kind, loading, row: resolved?.entity }),
    [actions, entity, loading, resolved?.entity, resolved?.kind],
  );

  if (!loading && !resolved) {
    return <Redirect to={redirectUrl} />;
  }

  if (isFinished) {
    return null;
  }

  return (
    <BonusDetailContext.Provider value={value}>
      <div className="bonus-details">
        <BonusDetailHeader />
        <BonusDetailWager />
        <BonusDetailInfo />
        <BonusDetailBet />
        <BonusEligibleGames />
        <BonusDetailTerms />
      </div>
    </BonusDetailContext.Provider>
  );
};

BonusDetailController.propTypes = {
  activateBonus: PropTypes.func,
  activateFreespin: PropTypes.func,
  availableTemplates: PropTypes.array.isRequired,
  bonusId: PropTypes.string.isRequired,
  cancelFreespin: PropTypes.func,
  clientBonuses: PropTypes.array.isRequired,
  deactivateBonus: PropTypes.func,
  freespins: PropTypes.array.isRequired,
  listUrl: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default BonusDetailController;
